import React from 'react';
import {Bars3Icon} from '@heroicons/react-v2/24/outline';

import Sidebar from './Sidebar';
import MobileNavDrawer from './MobileNavDrawer';
import {SidebarProvider, useSidebar} from './SidebarContext';
import SkipLink from '../SkipLink';
import Link from '../Link';
import {BrandMark, getBrandName} from '../Brand';
import {cx} from '../../util';

/**
 * The bar shown below `lg`, where the sidebar does not exist: just the drawer
 * trigger and the brand. Search and the account live inside the drawer.
 */
const MobileTopBar = () => {
  const {setMobileOpen} = useSidebar();

  return (
    <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/85 backdrop-blur-md lg:hidden">
      <div className="flex h-14 items-center gap-2 px-4">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation menu"
          className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100"
        >
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
        <Link href="/home?src=nav" aria-label={`${getBrandName()} home`} className="mr-1 flex shrink-0 items-center">
          <BrandMark className="h-6 w-6" />
        </Link>
      </div>
    </header>
  );
};

/**
 * The logged-in application shell: a sticky full-height sidebar that carries
 * all navigation — nav sections, search, the upgrade CTA and the account row.
 * There is no desktop top bar; below `lg` a minimal bar carries the drawer
 * trigger.
 *
 * The sidebar is `sticky` rather than `fixed` so the document keeps normal
 * window scrolling — page banners, sticky sections and scroll restoration all
 * behave as they did under the old top header. When the sidebar is collapsed
 * and expands on hover it does so as an overlay, without reflowing content.
 *
 * @param {node} props.children Page content, rendered inside <main>.
 * @param {node} [props.footer] Rendered after <main>, inside the content column.
 * @param {node} [props.banners] Rendered above the shell, full width — the
 *   same slot the legacy header gives them.
 * @param {string} [props.upgradeLink] Destination for the sidebar upgrade CTA.
 * @param {boolean} [props.forceCollapsed] Pin the sidebar to its icon rail.
 * @param {boolean} [props.initialCollapsed] Server-read cookie value, so the
 *   first paint is already the right width.
 *   Lesson pages use this because they stack a course sidebar beside it.
 * @param {boolean} [props.fillViewport] Make the content column exactly one
 *   viewport tall and hand scrolling to the page itself, for panelled layouts
 *   like lessons that manage their own overflow.
 * @param {string} [props.mainClassName]
 */
const AppShell = ({
  children,
  footer,
  banners,
  upgradeLink,
  forceCollapsed = false,
  initialCollapsed,
  fillViewport = false,
  mainClassName,
}) => (
  <SidebarProvider forceCollapsed={forceCollapsed} initialCollapsed={initialCollapsed}>
    <SkipLink />
    {banners}
    <div className="flex min-h-screen bg-white">
      <Sidebar upgradeLink={upgradeLink} />
      <MobileNavDrawer upgradeLink={upgradeLink} />

      <div className={cx('flex min-w-0 flex-1 flex-col', fillViewport && 'h-screen overflow-hidden')}>
        <MobileTopBar />
        <main
          id="main-content"
          tabIndex="-1"
          className={cx(
            'min-w-0',
            fillViewport ? 'min-h-0 flex-1' : 'flex-1',
            mainClassName,
          )}
        >
          {children}
        </main>
        {footer}
      </div>
    </div>
  </SidebarProvider>
);

export default AppShell;
